import { cn } from "@/lib/utils/cn";
import { Input } from "@/ui-components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/ui-components/ui/tooltip";
import { Info, Search } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { Table } from "@tanstack/react-table";
import { DataTable2Columns } from "./data-table-2-columns";

export interface DataTable2ToolbarProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  searchHint?: ReactNode;
  infoText?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  searchDebounceMs?: number;
  className?: string;
  table?: Table<any>;
  showColumnToggle?: boolean;
  showSearch?: boolean;
  left?: ReactNode;
  right?: ReactNode;
  columnTogglePlacement?: "start" | "end";
  columnTogglePosition?: "left" | "right";
}

export function DataTable2Toolbar({
  searchQuery,
  onSearchChange,
  searchPlaceholder = "Suchen...",
  searchHint,
  infoText,
  leading,
  trailing,
  searchDebounceMs = 400,
  className,
  table,
  showColumnToggle = true,
  showSearch,
  left,
  right,
  columnTogglePlacement = "end",
  columnTogglePosition = "right",
}: DataTable2ToolbarProps) {
  const resolvedShowSearch = showSearch ?? Boolean(onSearchChange);
  const [localQuery, setLocalQuery] = useState(searchQuery ?? "");

  useEffect(() => {
    setLocalQuery(searchQuery ?? "");
  }, [searchQuery]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (!resolvedShowSearch) return;
      if (!onSearchChange) return;
      if ((searchQuery ?? "") !== localQuery) onSearchChange(localQuery);
    }, searchDebounceMs);

    return () => window.clearTimeout(id);
  }, [localQuery, onSearchChange, resolvedShowSearch, searchDebounceMs, searchQuery]);

  return (
    <div className={cn("bg-white border-b border-gray-100 px-4 py-4", className)}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
          {left}
          {table && showColumnToggle && columnTogglePosition === "left" ? <DataTable2Columns table={table} /> : null}
          {resolvedShowSearch ? (
            <div className="w-full sm:w-80">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  placeholder={searchPlaceholder}
                  value={localQuery}
                  onChange={(e) => setLocalQuery(e.target.value)}
                  className={cn(
                    "pl-9 h-10 text-sm bg-white border border-gray-200 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-300 rounded-xl transition-colors",
                    searchHint ? "pr-9" : undefined,
                  )}
                />
                {searchHint ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" align="end">
                        {searchHint}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : null}
              </div>
            </div>
          ) : null}
          {trailing}
        </div>

        <div className="flex flex-wrap items-center justify-between lg:justify-end gap-3 shrink-0">
          {infoText ? <div className="text-sm text-gray-500">{infoText}</div> : null}
          {right}
          {leading}
          {table && showColumnToggle && columnTogglePosition === "right" && columnTogglePlacement === "start" ? (
            <DataTable2Columns table={table} />
          ) : null}
          {table && showColumnToggle && columnTogglePosition === "right" && columnTogglePlacement === "end" ? (
            <DataTable2Columns table={table} />
          ) : null}
        </div>
      </div>
    </div>
  );
}
